import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Col, Row, Progress } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';

import FileSize from 'shared/helpers/fileSize';
import { Button, ButtonIcon } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import { toBase64, ALERT_TYPE_ERROR } from 'shared/utils';
import { showAlert } from 'redux/actions/alertActions';

import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-refresh.svg';

import { UploadDocumentsWrapper } from './styles';

const INVALID_EXTENSION = '0';

const UploadDocuments = ({
  onCancel,
  setNewUploadFiles,
  newUploadFiles,
  currentPath,
  refFileManager,
}) => {
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { t } = useTranslation();
  const { cusNavId, clientId, mail, name, surname, isCustomer } = useSelector(
    (state) => state.auth
  );
  const { currentClient } = useSelector((state) => state.clientState);
  const [selectedIndex, setSelectedIndex] = useState();
  const [isUploadSave, setIsUploadSave] = useState(false);
  let newDataFiles = [];

  const [progress, setProgress] = useState(0);

  const [, createDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, documentMigrations] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onCreateDocument = async (fileWithoutDirectories) => {
    try {
      const newFilesPromises = fileWithoutDirectories
        .filter((x) => x.statusCode !== INVALID_EXTENSION && x.size !== 0)
        .map((newUploadFile) => {
          return new Promise((resolve) => {
            toBase64(newUploadFile.rawFile).then((curretFileBase64) => {
              resolve({
                name: newUploadFile.name,
                description: '',
                tags: [],
                state: '',
                size: newUploadFile.size,
                pathId: null,
                parentPathId: currentPath,
                data: curretFileBase64,
                createdBy: { nameComplete: `${name} ${surname}`, mail },
              });
            });
          });
        });
      const newData = await Promise.all(newFilesPromises);
      let documentData;
      if (newData.length) {
        const {
          data: { data },
        } = await createDocument({
          data: newData,
          url: `${window.API_V1}/cli/items/${cusNavId}/${
            isCustomer ? currentClient.clientId : clientId
          }/${encodeURIComponent(mail)}`,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setProgress(Math.max(0, percent - 1));
          },
        });
        documentData = data;
      }
      const UploadInvalidFilter = fileWithoutDirectories.filter(
        (x) => x.statusCode === INVALID_EXTENSION || x.size === 0
      );
      if (documentData) {
        newDataFiles = [...documentData, ...newDataFiles, ...UploadInvalidFilter];
      } else {
        newDataFiles = [...newDataFiles, ...UploadInvalidFilter];
      }
      setNewUploadFiles([...newDataFiles]);
      setIsUploadSave(true);
    } catch (error) {
      setNewUploadFiles([]);
      newDataFiles = [];
      onCancel();
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: error.message || error.response?.data?.errors[0].errorMessage,
        })
      );
    }
  };

  const onCreateDirectoryDocument = async (directoryFiles) => {
    try {
      const newFilesPromises = directoryFiles
        .filter((x) => x.statusCode !== INVALID_EXTENSION && x.size !== 0)
        .map((newUploadFile) => {
          return new Promise((resolve) => {
            toBase64(newUploadFile.rawFile).then((curretFileBase64) => {
              resolve({
                name: newUploadFile.name,
                description: '',
                data: curretFileBase64,
                idTags: [],
                size: newUploadFile.size,
                version: 1,
                pathName: newUploadFile.pathName,
                createdBy: { nameComplete: `${name} ${surname}`, mail },
              });
            });
          });
        });
      const newData = await Promise.all(newFilesPromises);
      const {
        data: { data },
      } = await documentMigrations({
        data: newData,
        url: `${window.API_V1}/cli/items/migration/document/${cusNavId}/${
          isCustomer ? currentClient.clientId : clientId
        }?login=${encodeURIComponent(mail)}`,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(percent);
        },
      });
      const UploadInvalidFilter = directoryFiles.filter(
        (x) => x.statusCode === INVALID_EXTENSION || x.size === 0
      );
      newDataFiles = [...data, ...newDataFiles, ...UploadInvalidFilter];
      setNewUploadFiles([...newDataFiles]);
      setIsUploadSave(true);
    } catch (error) {
      setIsUploadSave(true);
      if (error.response?.data?.errors[0].code === 'CCIT-20') {
        setNewUploadFiles([]);
        newDataFiles = [];
        onCancel();
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: error.response?.data.errors[0].message,
          })
        );
      }
    }
  };

  useEffect(() => {
    const directoryFiles = newUploadFiles.filter((x) => x.isDirectory);
    const fileWithoutDirectories = newUploadFiles.filter((x) => !x.isDirectory);
    if (directoryFiles.length > 0) {
      onCreateDirectoryDocument(directoryFiles);
    }
    if (fileWithoutDirectories.length > 0) {
      onCreateDocument(fileWithoutDirectories);
    }
    return () => {
      newDataFiles = [];
    };
  }, []);

  const removeDocument = (index) => {
    const newFiles = newUploadFiles.filter((item, i) => i !== index);
    setNewUploadFiles(newFiles);
  };

  const documentRefresh = async (index) => {
    try {
      setSelectedIndex(index);
      setProgress(0);
      const newFiles = newUploadFiles.filter((item, i) => i === index);
      const newData = newFiles.map((file) => {
        return {
          name: file.name,
          description: '',
          tags: [],
          state: '',
          size: file.size,
          pathId: null,
          parentPathId: currentPath,
          data: file.data,
          createdBy: { nameComplete: `${name} ${surname}`, mail },
        };
      });
      const {
        data: { data },
      } = await createDocument({
        data: newData,
        url: `${window.API_V1}/cli/items/${cusNavId}/${
          isCustomer ? currentClient.clientId : clientId
        }/${encodeURIComponent(mail)}`,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(percent);
        },
      });
      const newFilesUpdated = newUploadFiles.map((x) => {
        if (x.id === data[0].id) {
          return { ...x, errorUpload: data[0].errorUpload };
        }
        return { ...x };
      });
      setNewUploadFiles([...newFilesUpdated]);
      setSelectedIndex();
      refFileManager.current.refresh();
    } catch (error) {
      console.error('getting document error: ', error);
    }
  };

  const Title = () => {
    return (
      <Row className="row-title mb-4">
        <Col sm={12} className="ml-n3">
          <h3>{t('documents_upload_document')}</h3>
        </Col>
      </Row>
    );
  };

  return (
    <UploadDocumentsWrapper>
      <Title />
      <div className="upload-info-container">
        {newUploadFiles.map((uploadFile, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="d-flex justify-content-between align-items-center mb-4" key={index}>
            <div className="upload-list">
              <p>{uploadFile.name}</p>
              <div className="d-flex justify-content-between">
                <span>{FileSize(uploadFile.size)}</span>
                {((uploadFile.errorUpload === null &&
                  uploadFile.statusCode !== INVALID_EXTENSION &&
                  uploadFile.size !== 0) ||
                  selectedIndex === index) && <span>{`${progress}%`}</span>}
              </div>
              {((uploadFile.errorUpload === null &&
                uploadFile.statusCode !== INVALID_EXTENSION &&
                uploadFile.size !== 0) ||
                selectedIndex === index) && (
                <Progress
                  className="my-2"
                  style={{
                    height: '3px',
                  }}
                  value={progress}
                  color="danger"
                />
              )}
              {uploadFile.errorUpload !== null &&
                uploadFile.errorUpload === false &&
                !selectedIndex && (
                  <span className="document-success-upload">{t('document_success_upload')}</span>
                )}
              {uploadFile.errorUpload !== null &&
                uploadFile.errorUpload === true &&
                !selectedIndex && (
                  <span className="document-fail-upload">{t('document_fail_upload')}</span>
                )}
              {uploadFile.size === 0 && !selectedIndex && (
                <div>
                  <span className="document-fail-upload">{t('new_document_file_null')}</span>
                </div>
              )}
              {uploadFile.statusCode === INVALID_EXTENSION && !selectedIndex && (
                <span className="document-fail-upload">{t('general_invalid_file_type')}</span>
              )}
            </div>
            {uploadFile.errorUpload !== null && uploadFile.errorUpload === true && (
              <div>
                <ButtonIcon
                  id={`refresh-icon-upload-document-${index}`}
                  title={t('general_button_refresh')}
                  type="button"
                  Icon={() => <RefreshIcon fill={colors.black} />}
                  className="button-icon ml-2"
                  onClick={() => documentRefresh(index)}
                />
                <ButtonIcon
                  id={`trash-icon-upload-document-${index}`}
                  title={t('general_delete')}
                  type="button"
                  Icon={() => <TrashIcon fill={colors.black} />}
                  className="button-icon ml-2"
                  onClick={() => removeDocument(index)}
                />
              </div>
            )}
            {(uploadFile.statusCode === INVALID_EXTENSION || uploadFile.size === 0) && (
              <div>
                <ButtonIcon
                  id={`trash-icon-upload-document-${index}`}
                  title={t('general_delete')}
                  type="button"
                  Icon={() => <TrashIcon fill={colors.black} />}
                  className="button-icon ml-2"
                  onClick={() => removeDocument(index)}
                />
              </div>
            )}
          </div>
        ))}
        {isUploadSave && (
          <div className="d-flex justify-content-end">
            <Button
              className="home__btn text-uppercase"
              type="button"
              label={t('general_close')}
              color="primary"
              onClick={() => {
                newDataFiles = [];
                onCancel();
                setNewUploadFiles([]);
              }}
            />
          </div>
        )}
      </div>
    </UploadDocumentsWrapper>
  );
};

UploadDocuments.defaultProps = {
  newUploadFiles: [],
  currentPath: '',
};

UploadDocuments.propTypes = {
  onCancel: PropTypes.func.isRequired,
  newUploadFiles: PropTypes.arrayOf(PropTypes.shape({})),
  currentPath: PropTypes.string,
  setNewUploadFiles: PropTypes.func.isRequired,
  refFileManager: PropTypes.objectOf(PropTypes.shape()).isRequired,
};

export default UploadDocuments;
